import { Collapse, Group, Stack, Text, UnstyledButton } from '@mantine/core';
import { useState } from 'react';

import type { CandidateResult } from '../hooks/useModelArena';
import { modelLabel } from '../runLabels';

import { type Column, DataTable, type Row } from './DataTable';
import { ExpandChevron } from './ExpandChevron';
import { readable } from './metricFormat';

type BreakdownTable = { columns: string[]; rows: (string | number)[][] };

/**
 * How the candidates scored, broken down the way the task chose to break it down.
 *
 * One table per breakdown for the whole run rather than one per candidate: the question these answer
 * is "what did this one miss that the other found", which is only answerable side by side. Nothing
 * here is interpreted, so a task can report whatever breakdown is worth reading.
 */
export function Breakdowns({ results }: { results: CandidateResult[] }) {
  const merged = mergeByName(results);
  const names = Object.keys(merged);
  if (names.length === 0) return null;

  return (
    <Stack gap={4} mt="sm">
      {names.map(name => (
        <Breakdown key={name} name={name} table={merged[name]} />
      ))}
    </Stack>
  );
}

/** The same breakdown from every candidate, stacked under one heading and labelled by model. */
export function mergeByName(results: CandidateResult[]): Record<string, BreakdownTable> {
  const merged: Record<string, BreakdownTable> = {};

  for (const result of results) {
    for (const [name, table] of Object.entries(result.breakdowns)) {
      merged[name] ??= { columns: ['model', ...table.columns], rows: [] };
      merged[name].rows.push(...table.rows.map(row => [modelLabel(result), ...row]));
    }
  }

  return merged;
}

function Breakdown({ name, table }: { name: string; table: BreakdownTable }) {
  const [open, setOpen] = useState(false);

  const columns: Column[] = table.columns.map((column, index) => ({
    key: column,
    label: column,
    numeric: table.rows.some(row => typeof row[index] === 'number'),
  }));
  const rows: Row[] = table.rows.map(row =>
    Object.fromEntries(table.columns.map((column, index) => [column, row[index]])),
  );

  return (
    <div>
      <UnstyledButton onClick={() => setOpen(showing => !showing)} aria-expanded={open}>
        <Group gap={4} wrap="nowrap">
          <ExpandChevron expanded={open} size={12} />
          <Text size="xs" c="dimmed">
            {readable(name)} ({table.rows.length})
          </Text>
        </Group>
      </UnstyledButton>
      <Collapse expanded={open}>
        <DataTable columns={columns} rows={rows} />
      </Collapse>
    </div>
  );
}
